import { useState, useEffect } from 'react';
import { Card, Tabs, Row, Col, Statistic, DatePicker, Select, Button, Space, Table, message, Spin } from 'antd';
import {
  DollarOutlined,
  CarOutlined,
  UserOutlined,
  DownloadOutlined,
  RiseOutlined,
  FallOutlined,
  EnvironmentOutlined,
  ShoppingOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { Line, Column, Pie } from '@ant-design/charts';
import dayjs, { Dayjs } from 'dayjs';
import { apiClient } from '../../lib/api-client';
import { useService } from '../../contexts/ServiceContext';

const { RangePicker } = DatePicker;

// ============ TAXI TYPES ============

interface TaxiAnalyticsData {
  revenue: {
    daily: { date: string; amount: number }[];
    weekly: { week: string; amount: number }[];
    monthly: { month: string; amount: number }[];
    total: number;
    growth: number;
  };
  trips: {
    daily: { date: string; count: number; completed: number; cancelled: number }[];
    byType: { type: string; count: number }[];
    total: number;
    growth: number;
  };
  users: {
    daily: { date: string; newUsers: number; newDrivers: number }[];
    total: number;
    totalDrivers: number;
    growth: number;
  };
  drivers: {
    topDrivers: {
      id: number;
      fullName: string;
      phone: string;
      totalTrips: number;
      completedTrips: number;
      rating: number;
      earnings: number;
    }[];
    byStatus: { status: string; count: number }[];
    averageRating: number;
    averageTrips: number;
  };
  regions: {
    name: string;
    trips: number;
    revenue: number;
    drivers: number;
  }[];
}

// ============ BUS TYPES ============

interface BusAnalyticsData {
  revenue: {
    total: number;
    paid: number;
    unpaid: number;
    commission: number;
    growth: number;
    daily: { date: string; amount: number; paid: number; bookings: number; seats: number }[];
  };
  trips: {
    total: number;
    completed: number;
    cancelled: number;
    totalSeats: number;
    bookedSeats: number;
    occupancyRate: number;
    growth: number;
    daily: { date: string; count: number; completed: number; cancelled: number; seats: number; booked: number }[];
  };
  bookings: {
    total: number;
    totalSeats: number;
  };
  routes: { route: string; revenue: number; bookings: number; seats: number }[];
  companies: { name: string; trips: number; bookings: number; revenue: number; seats: number; booked: number }[];
}

// ============ HELPERS ============

const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: 'Shahar ichida taksi',
  INTERREGION_TAXI: 'Shaharlararo taksi',
  POSTAGE: 'Pochta',
  CARGO: 'Yuk tashish',
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
};

const statFontSize = { fontSize: 20 };

const GrowthStat = ({ title, value }: { title: string; value: number }) => (
  <Card>
    <Statistic
      title={title}
      value={value}
      suffix="%"
      prefix={value >= 0 ? <RiseOutlined style={{ color: '#27AE60' }} /> : <FallOutlined style={{ color: '#E74C3C' }} />}
      valueStyle={{ color: value >= 0 ? '#27AE60' : '#E74C3C', ...statFontSize }}
    />
  </Card>
);

const EmptyChart = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
    <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
    <p>{text}</p>
  </div>
);

// ============ BUS ANALYTICS COMPONENT ============

const BusAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<BusAnalyticsData | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs(),
  ]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/bus/analytics', {
        params: {
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
        },
      });
      setData(response.data);
    } catch {
      message.error("Analitika ma'lumotlarini yuklashda xatolik");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  // Revenue Tab
  const RevenueTab = () => {
    const chartData = data?.revenue.daily.map(d => ({ date: d.date, value: d.amount })) || [];

    return (
      <div>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Jami daromad"
                value={data?.revenue.total || 0}
                prefix={<DollarOutlined style={{ color: '#1ABC9C' }} />}
                suffix="so'm"
                valueStyle={statFontSize}
                formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Card>
              <Statistic
                title="To'langan"
                value={data?.revenue.paid || 0}
                suffix="so'm"
                valueStyle={{ color: '#27AE60', ...statFontSize }}
                formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Card>
              <Statistic
                title="Komissiya"
                value={data?.revenue.commission || 0}
                suffix="so'm"
                valueStyle={{ color: '#9B59B6', ...statFontSize }}
                formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={5}>
            <Card>
              <Statistic
                title="O'rtacha kunlik"
                value={
                  data?.revenue.daily && data.revenue.daily.length > 0
                    ? Math.round(data.revenue.daily.reduce((s, d) => s + d.amount, 0) / data.revenue.daily.length)
                    : 0
                }
                suffix="so'm"
                prefix={<DollarOutlined style={{ color: '#3498DB' }} />}
                valueStyle={statFontSize}
                formatter={(val) => new Intl.NumberFormat('uz-UZ').format(val as number)}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={5}>
            <GrowthStat title="O'sish" value={data?.revenue.growth || 0} />
          </Col>
        </Row>
        <Card title="Daromad dinamikasi">
          {chartData.length > 0 ? (
            <Line
              data={chartData}
              xField="date"
              yField="value"
              smooth
              point={{ size: 3 }}
              color="#1ABC9C"
              yAxis={{ label: { formatter: (v: string) => formatCurrency(Number(v)) } }}
              tooltip={{ formatter: (datum: { value: number }) => ({ name: 'Daromad', value: formatCurrency(datum.value) }) }}
            />
          ) : (
            <EmptyChart icon={<DollarOutlined />} text="Tanlangan davr uchun daromad ma'lumotlari topilmadi" />
          )}
        </Card>
      </div>
    );
  };

  // Trips Tab
  const TripsTab = () => {
    const tripChartData = data?.trips.daily.flatMap(d => [
      { date: d.date, type: 'Jami', value: d.count },
      { date: d.date, type: 'Yakunlangan', value: d.completed },
      { date: d.date, type: 'Bekor qilingan', value: d.cancelled },
    ]) || [];

    return (
      <div>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6} md={4}>
            <Card>
              <Statistic title="Jami reyslar" value={data?.trips.total || 0} prefix={<CarOutlined style={{ color: '#1ABC9C' }} />} valueStyle={statFontSize} />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Card>
              <Statistic title="Yakunlangan" value={data?.trips.completed || 0} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#27AE60', ...statFontSize }} />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Card>
              <Statistic title="Bekor qilingan" value={data?.trips.cancelled || 0} prefix={<CloseCircleOutlined />} valueStyle={{ color: '#E74C3C', ...statFontSize }} />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Card>
              <Statistic
                title="Band o'rinlar"
                value={data?.trips.bookedSeats || 0}
                suffix={` / ${data?.trips.totalSeats || 0}`}
                valueStyle={statFontSize}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Card>
              <Statistic
                title="Band foizi"
                value={data?.trips.occupancyRate || 0}
                suffix="%"
                valueStyle={{ color: (data?.trips.occupancyRate || 0) >= 70 ? '#27AE60' : '#E67E22', ...statFontSize }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <GrowthStat title="O'sish" value={data?.trips.growth || 0} />
          </Col>
        </Row>
        <Card title="Reyslar dinamikasi">
          {tripChartData.length > 0 ? (
            <Line
              data={tripChartData}
              xField="date"
              yField="value"
              seriesField="type"
              smooth
              point={{ size: 3 }}
              color={['#1ABC9C', '#27AE60', '#E74C3C']}
            />
          ) : (
            <EmptyChart icon={<CarOutlined />} text="Tanlangan davr uchun reys ma'lumotlari topilmadi" />
          )}
        </Card>
      </div>
    );
  };

  // Routes Tab
  const RoutesTab = () => {
    const routeData = data?.routes || [];
    const chartData = routeData.map(r => ({ route: r.route, value: r.revenue }));
    const columns = [
      { title: "Yo'nalish", dataIndex: 'route', key: 'route' },
      { title: 'Daromad', dataIndex: 'revenue', key: 'revenue', render: (v: number) => formatCurrency(v), sorter: (a: { revenue: number }, b: { revenue: number }) => a.revenue - b.revenue },
      { title: 'Buyurtmalar', dataIndex: 'bookings', key: 'bookings', sorter: (a: { bookings: number }, b: { bookings: number }) => a.bookings - b.bookings },
      { title: "O'rindiqlar", dataIndex: 'seats', key: 'seats' },
    ];

    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card title="Yo'nalishlar bo'yicha daromad">
              {chartData.length > 0 ? (
                <Column data={chartData} xField="route" yField="value" color="#9B59B6" xAxis={{ label: { autoRotate: true } }} />
              ) : (
                <EmptyChart icon={<EnvironmentOutlined />} text="Ma'lumot topilmadi" />
              )}
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Yo'nalishlar statistikasi">
              <Table dataSource={routeData} columns={columns} rowKey="route" pagination={false} size="small" scroll={{ y: 300 }} />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Companies Tab
  const CompaniesTab = () => {
    const companies = data?.companies || [];
    const pieData = companies.map(c => ({ type: c.name, value: c.revenue }));
    const columns = [
      { title: 'Kompaniya', dataIndex: 'name', key: 'name' },
      { title: 'Reyslar', dataIndex: 'trips', key: 'trips' },
      { title: 'Buyurtmalar', dataIndex: 'bookings', key: 'bookings' },
      { title: 'Daromad', dataIndex: 'revenue', key: 'revenue', render: (v: number) => formatCurrency(v), sorter: (a: { revenue: number }, b: { revenue: number }) => a.revenue - b.revenue },
      {
        title: "Band foizi",
        key: 'occupancy',
        render: (_: unknown, r: { seats: number; booked: number }) =>
          r.seats > 0 ? `${Math.round((r.booked / r.seats) * 100)}%` : '-',
      },
    ];

    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Card title="Daromad bo'yicha ulush">
              {pieData.length > 0 && pieData.some(p => p.value > 0) ? (
                <Pie data={pieData} angleField="value" colorField="type" radius={0.8} innerRadius={0.6} label={{ type: 'outer' }} interactions={[{ type: 'element-active' }]} />
              ) : (
                <EmptyChart icon={<BankOutlined />} text="Ma'lumot topilmadi" />
              )}
            </Card>
          </Col>
          <Col span={16}>
            <Card title="Kompaniyalar statistikasi">
              <Table dataSource={companies} columns={columns} rowKey="name" pagination={false} size="small" />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const tabItems = [
    { key: 'revenue', label: <span><DollarOutlined /> Daromad</span>, children: <RevenueTab /> },
    { key: 'trips', label: <span><CarOutlined /> Reyslar ({data?.trips.total || 0})</span>, children: <TripsTab /> },
    { key: 'routes', label: <span><EnvironmentOutlined /> Yo'nalishlar</span>, children: <RoutesTab /> },
    { key: 'companies', label: <span><BankOutlined /> Kompaniyalar</span>, children: <CompaniesTab /> },
  ];

  if (loading && !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}>
          <EnvironmentOutlined style={{ marginRight: 8 }} />
          Avtobus analitikasi
        </h2>
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => dates && setDateRange(dates as [Dayjs, Dayjs])}
            format="DD.MM.YYYY"
          />
        </Space>
      </div>
      <Tabs items={tabItems} />
    </div>
  );
};

// ============ TAXI ANALYTICS COMPONENT ============

const TaxiAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<TaxiAnalyticsData | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs(),
  ]);
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [exporting, setExporting] = useState(false);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/analytics', {
        params: {
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
          period,
        },
      });
      setData(response.data);
    } catch {
      message.error("Analitika ma'lumotlarini yuklashda xatolik");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, period]);

  const handleExport = async (type: 'revenue' | 'trips' | 'users' | 'all') => {
    setExporting(true);
    try {
      const response = await apiClient.get('/admin/analytics/export', {
        params: {
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
          type,
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics-${type}-${dayjs().format('YYYY-MM-DD')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Hisobot yuklandi');
    } catch {
      message.error('Eksport qilishda xatolik');
    } finally {
      setExporting(false);
    }
  };

  const RevenueTab = () => {
    const revenueData = period === 'daily' ? data?.revenue.daily : period === 'weekly' ? data?.revenue.weekly : data?.revenue.monthly;
    const chartData = revenueData?.map(item => ({
      date: 'date' in item ? item.date : 'week' in item ? item.week : (item as any).month,
      value: item.amount,
    })) || [];

    return (
      <div>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic title="Jami daromad" value={data?.revenue.total || 0} formatter={(val) => formatCurrency(Number(val))} prefix={<DollarOutlined style={{ color: '#1ABC9C' }} />} valueStyle={statFontSize} />
            </Card>
          </Col>
          <Col span={8}>
            <GrowthStat title="O'sish" value={data?.revenue.growth || 0} />
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="O'rtacha kunlik"
                value={data?.revenue.daily && data.revenue.daily.length > 0 ? Math.round(data.revenue.daily.reduce((s, d) => s + d.amount, 0) / data.revenue.daily.length) : 0}
                formatter={(val) => formatCurrency(Number(val))}
                prefix={<DollarOutlined style={{ color: '#3498DB' }} />}
                valueStyle={statFontSize}
              />
            </Card>
          </Col>
        </Row>
        <Card title="Daromad dinamikasi">
          {chartData.length > 0 ? (
            <Line data={chartData} xField="date" yField="value" smooth point={{ size: 3 }} color="#1ABC9C" yAxis={{ label: { formatter: (v: string) => formatCurrency(Number(v)) } }} tooltip={{ formatter: (datum: { value: number }) => ({ name: 'Daromad', value: formatCurrency(datum.value) }) }} />
          ) : (
            <EmptyChart icon={<DollarOutlined />} text="Tanlangan davr uchun daromad ma'lumotlari topilmadi" />
          )}
        </Card>
      </div>
    );
  };

  const TripsTab = () => {
    const tripData = data?.trips.daily.flatMap(item => [
      { date: item.date, type: 'Jami', value: item.count },
      { date: item.date, type: 'Bajarilgan', value: item.completed },
      { date: item.date, type: 'Bekor qilingan', value: item.cancelled },
    ]) || [];
    const pieData = data?.trips.byType.map(item => ({ type: tripTypeLabels[item.type] || item.type, value: item.count })) || [];

    return (
      <div>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={8}><Card><Statistic title="Jami sayohatlar" value={data?.trips.total || 0} prefix={<CarOutlined style={{ color: '#1ABC9C' }} />} valueStyle={statFontSize} /></Card></Col>
          <Col span={8}><GrowthStat title="O'sish" value={data?.trips.growth || 0} /></Col>
          <Col span={8}>
            <Card>
              <Statistic title="O'rtacha kunlik" value={data?.trips.daily && data.trips.daily.length > 0 ? Math.round(data.trips.daily.reduce((s, d) => s + d.count, 0) / data.trips.daily.length) : 0} prefix={<CarOutlined style={{ color: '#3498DB' }} />} valueStyle={statFontSize} />
            </Card>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={16}>
            <Card title="Sayohatlar dinamikasi">
              {tripData.length > 0 ? <Line data={tripData} xField="date" yField="value" seriesField="type" smooth point={{ size: 3 }} color={['#1ABC9C', '#27AE60', '#E74C3C']} /> : <EmptyChart icon={<CarOutlined />} text="Ma'lumot topilmadi" />}
            </Card>
          </Col>
          <Col span={8}>
            <Card title="Sayohat turlari">
              {pieData.length > 0 ? <Pie data={pieData} angleField="value" colorField="type" radius={0.8} innerRadius={0.6} label={{ type: 'outer' }} interactions={[{ type: 'element-active' }]} /> : <EmptyChart icon={<CarOutlined />} text="Ma'lumot topilmadi" />}
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const UsersTab = () => {
    const userData = data?.users.daily.flatMap(item => [
      { date: item.date, type: 'Yangi foydalanuvchilar', value: item.newUsers },
      { date: item.date, type: 'Yangi haydovchilar', value: item.newDrivers },
    ]) || [];
    return (
      <div>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={8}><Card><Statistic title="Jami foydalanuvchilar" value={data?.users.total || 0} prefix={<UserOutlined style={{ color: '#1ABC9C' }} />} valueStyle={statFontSize} /></Card></Col>
          <Col span={8}><Card><Statistic title="Jami haydovchilar" value={data?.users.totalDrivers || 0} prefix={<CarOutlined style={{ color: '#3498DB' }} />} valueStyle={statFontSize} /></Card></Col>
          <Col span={8}><GrowthStat title="Foydalanuvchi o'sishi" value={data?.users.growth || 0} /></Col>
        </Row>
        <Card title="Foydalanuvchi o'sishi">
          {userData.length > 0 ? <Column data={userData} xField="date" yField="value" seriesField="type" isGroup color={['#1ABC9C', '#3498DB']} /> : <EmptyChart icon={<UserOutlined />} text="Ma'lumot topilmadi" />}
        </Card>
      </div>
    );
  };

  const DriversTab = () => {
    const columns = [
      { title: '#', key: 'index', width: 50, render: (_: unknown, __: unknown, index: number) => index + 1 },
      { title: 'Haydovchi', key: 'driver', render: (_: unknown, record: TaxiAnalyticsData['drivers']['topDrivers'][0]) => (<div><div style={{ fontWeight: 500 }}>{record.fullName || "Ismi yo'q"}</div><div style={{ fontSize: 12, color: '#7F8C8D' }}>{record.phone}</div></div>) },
      { title: 'Sayohatlar', dataIndex: 'totalTrips', key: 'totalTrips' },
      { title: 'Bajarilgan', dataIndex: 'completedTrips', key: 'completedTrips', render: (val: number, record: TaxiAnalyticsData['drivers']['topDrivers'][0]) => <span>{val} ({record.totalTrips > 0 ? Math.round((val / record.totalTrips) * 100) : 0}%)</span> },
      { title: 'Reyting', dataIndex: 'rating', key: 'rating', render: (val: number) => val.toFixed(1) },
      { title: 'Daromad', dataIndex: 'earnings', key: 'earnings', render: (val: number) => formatCurrency(val) },
    ];
    const statusData = data?.drivers.byStatus.map(item => ({ status: item.status === 'ONLINE' ? 'Online' : item.status === 'OFFLINE' ? 'Offline' : item.status, value: item.count })) || [];
    return (
      <div>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={8}><Card><Statistic title="O'rtacha reyting" value={data?.drivers.averageRating || 0} precision={2} prefix={<CarOutlined style={{ color: '#1ABC9C' }} />} valueStyle={statFontSize} /></Card></Col>
          <Col span={8}><Card><Statistic title="O'rtacha sayohatlar" value={data?.drivers.averageTrips || 0} precision={1} prefix={<CarOutlined style={{ color: '#3498DB' }} />} valueStyle={statFontSize} /></Card></Col>
          <Col span={8}>
            <Card title="Haydovchi holati" styles={{ body: { padding: 16 } }}>
              {statusData.length > 0 ? <Pie data={statusData} angleField="value" colorField="status" radius={0.8} height={150} legend={false} label={{ type: 'inner', content: '{name}: {value}' }} /> : <EmptyChart icon={<UserOutlined />} text="Ma'lumot topilmadi" />}
            </Card>
          </Col>
        </Row>
        <Card title="Top haydovchilar"><Table dataSource={data?.drivers.topDrivers || []} columns={columns} rowKey="id" pagination={false} size="small" /></Card>
      </div>
    );
  };

  const RegionsTab = () => {
    const regionData = data?.regions || [];
    const columns = [
      { title: 'Viloyat', dataIndex: 'name', key: 'name' },
      { title: 'Sayohatlar', dataIndex: 'trips', key: 'trips', sorter: (a: { trips: number }, b: { trips: number }) => a.trips - b.trips },
      { title: 'Daromad', dataIndex: 'revenue', key: 'revenue', render: (val: number) => formatCurrency(val), sorter: (a: { revenue: number }, b: { revenue: number }) => a.revenue - b.revenue },
      { title: 'Haydovchilar', dataIndex: 'drivers', key: 'drivers', sorter: (a: { drivers: number }, b: { drivers: number }) => a.drivers - b.drivers },
    ];
    const chartData = regionData.map(item => ({ region: item.name, value: item.trips }));
    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card title="Viloyatlar bo'yicha sayohatlar">
              {chartData.length > 0 ? <Column data={chartData} xField="region" yField="value" color="#1ABC9C" xAxis={{ label: { autoRotate: true } }} /> : <EmptyChart icon={<EnvironmentOutlined />} text="Ma'lumot topilmadi" />}
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Viloyatlar statistikasi"><Table dataSource={regionData} columns={columns} rowKey="name" pagination={false} size="small" scroll={{ y: 300 }} /></Card>
          </Col>
        </Row>
      </div>
    );
  };

  const tabItems = [
    { key: 'revenue', label: 'Daromad', children: <RevenueTab /> },
    { key: 'trips', label: 'Sayohatlar', children: <TripsTab /> },
    { key: 'users', label: 'Foydalanuvchilar', children: <UsersTab /> },
    { key: 'drivers', label: 'Haydovchilar', children: <DriversTab /> },
    { key: 'regions', label: 'Viloyatlar', children: <RegionsTab /> },
  ];

  if (loading && !data) {
    return (<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}><Spin size="large" /></div>);
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}><EnvironmentOutlined style={{ marginRight: 8 }} /> Analitika</h2>
        <Space>
          <RangePicker value={dateRange} onChange={(dates) => dates && setDateRange(dates as [Dayjs, Dayjs])} format="YYYY-MM-DD" />
          <Select value={period} onChange={setPeriod} style={{ width: 120 }} options={[{ value: 'daily', label: 'Kunlik' }, { value: 'weekly', label: 'Haftalik' }, { value: 'monthly', label: 'Oylik' }]} />
          <Button type="primary" icon={<DownloadOutlined />} onClick={() => handleExport('all')} loading={exporting}>Excel yuklab olish</Button>
        </Space>
      </div>
      <Tabs items={tabItems} />
    </div>
  );
};

// ============ MAIN EXPORT ============

export const AnalyticsDashboard = () => {
  const { selectedService } = useService();
  return selectedService === 'bus' ? <BusAnalyticsDashboard /> : <TaxiAnalyticsDashboard />;
};
